// Layer: presentation. Audit feed (FR-OC-3, Rules §4.3).
import { useCallback, useEffect, useState } from "react";
import type { StoriaApi } from "../application/ports";
import type { AuditFeed } from "../domain/types";
import { ErrorPanel, messageOf } from "./ShiftView";

interface Props {
  api: StoriaApi;
  limit?: number;
}

export function AuditView({ api, limit = 100 }: Props) {
  const [feed, setFeed] = useState<AuditFeed | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .loadAudit(limit)
      .then(setFeed)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api, limit]);

  useEffect(load, [load]);

  if (error) return <ErrorPanel message={error} onRetry={load} />;
  if (!feed) return <p className="muted" role="status">Loading audit feed…</p>;

  return (
    <main aria-label="Audit feed" className="stack">
      <header className="panel-head">
        <h2>Audit</h2>
        <p className="muted">{feed.entries.length} most recent entries</p>
      </header>

      {/* The append-only claim is only worth anything if someone checks it, so the
          chain verdict is shown as a first-class fact rather than assumed. */}
      <div
        className={feed.chain_intact ? "chain chain-ok" : "chain chain-broken"}
        role={feed.chain_intact ? "status" : "alert"}
      >
        {feed.chain_intact
          ? "Hash chain verified. No entry has been altered or removed."
          : "Hash chain FAILED verification. Treat this log as untrustworthy and escalate."}
      </div>

      {feed.entries.length === 0 ? (
        <p className="muted">No audit entries yet.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Actor</th>
                <th scope="col">Action</th>
                <th scope="col">Correlation</th>
              </tr>
            </thead>
            <tbody>
              {feed.entries.map((entry) => (
                <tr key={entry.digest}>
                  <td className="muted">{entry.emitted_at ?? "not recorded"}</td>
                  <td>{entry.actor}</td>
                  <td>{entry.action}</td>
                  <td className="mono">{entry.correlation_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
